import { createContext, useContext, useState, FC, ReactNode, useMemo } from "react";

export enum NewUserStepEnum {
  SendEmail = 0,
  InsertCode = 1,
  CompleteRegistration = 2,
  RegistrationComplete = 3,
  AcceptOrRejectInvitation = 4
}

interface NewUserFlowContextProps {
  currentStep: NewUserStepEnum;
  setCurrentStep: (step: NewUserStepEnum) => void;
  email: string | null;
  setEmail: (email: string | null) => void;
  workflowId: string | null;
  setWorkflowId: (workflowId: string | null) => void;
  resetFlow: () => void;
  setLanguage: (language: string | null) => void;
  language: string | null;
}

const NewUserFlowContext = createContext<NewUserFlowContextProps | undefined>(undefined);

export const useNewUserFlow = () => {
  const context = useContext(NewUserFlowContext);
  if (!context) {
    throw new Error("useNewUserFlow must be used within a NewUserFlowProvider");
  }
  return context;
};

export const NewUserFlowProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState<NewUserStepEnum>(NewUserStepEnum.SendEmail);
  const [email, setEmail] = useState<string | null>(null);
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [language, setLanguage] = useState<string | null>(null);

  const resetFlow = () => {
    setCurrentStep(NewUserStepEnum.SendEmail);
    setEmail(null);
    setWorkflowId(null);
    setLanguage(null);
  };

  const contextValue = useMemo(
    () => ({
      currentStep,
      setCurrentStep,
      email,
      setEmail,
      workflowId,
      setWorkflowId,
      resetFlow,
      setLanguage,
      language
    }),
    [currentStep, email, workflowId, language]
  );

  return <NewUserFlowContext.Provider value={contextValue}>{children}</NewUserFlowContext.Provider>;
};
